import { AgnesButton, cn } from "@agnes/ui";
import { Check, Pencil } from "lucide-react";
import { BaseComponentProps } from "@/components/BaseComponentProps";

export interface BarraSelecaoRubricasProps extends BaseComponentProps {
  quantidade: number;
  descricoes: number;
  /** Aprovação só existe para rubricas ainda não analisadas. */
  podeAprovar: boolean;
  aprovando?: boolean;
  onLimpar: () => void;
  onEditar: () => void;
  onAprovar: () => void;
}

// Estilo do protótipo: fundo roxo da marca, botões translúcidos e aprovação em verde.
const BOTAO_TRANSLUCIDO =
  "border border-white/25 bg-white/10 text-white hover:bg-white/20 hover:text-white";

export function BarraSelecaoRubricas({
  quantidade,
  descricoes,
  podeAprovar,
  aprovando = false,
  onLimpar,
  onEditar,
  onAprovar,
  className,
  dataQa = "rubricas-selecao-barra",
}: BarraSelecaoRubricasProps) {
  if (quantidade === 0) return null;

  const plural = quantidade > 1;
  const origem =
    descricoes > 1
      ? "selecionadas em múltiplas descrições"
      : `${plural ? "selecionadas" : "selecionada"} em 1 descrição`;

  return (
    // Pílula centralizada na largura do conteúdo. A folga lateral (max-w) evita que ela
    // passe por baixo do botão fixo da central de atendimento (NeoAssist, bottom-right).
    <div
      data-qa={dataQa}
      className={cn("pointer-events-none sticky bottom-4 z-30 mt-4 flex justify-center", className)}
    >
      <div
        role="toolbar"
        aria-label="Ações das rubricas selecionadas"
        className="pointer-events-auto flex w-fit max-w-[calc(100%-11rem)] flex-wrap items-center justify-between gap-x-10 gap-y-3 rounded-2xl bg-agnes-primary px-5 py-3 text-white shadow-agnes-panel"
      >
        <div className="flex items-center gap-3">
          <span
            data-qa={`${dataQa}-total`}
            className="rounded-md bg-[#00FF99] px-2 py-1 text-sm font-semibold text-agnes-primary-dark"
          >
            {quantidade.toLocaleString("pt-BR")} {plural ? "rubricas" : "rubrica"}
          </span>
          <span data-qa={`${dataQa}-origem`} className="text-sm text-white/90">
            {origem}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <AgnesButton
            dataQa={`${dataQa}-limpar`}
            variant="outlined"
            className={BOTAO_TRANSLUCIDO}
            disabled={aprovando}
            onClick={onLimpar}
          >
            Limpar
          </AgnesButton>

          <AgnesButton
            dataQa={`${dataQa}-editar`}
            variant="outlined"
            className={podeAprovar ? BOTAO_TRANSLUCIDO : "border-white bg-white text-agnes-primary"}
            startIcon={<Pencil className="h-4 w-4" />}
            disabled={aprovando}
            onClick={onEditar}
          >
            {podeAprovar
              ? "Editar em massa"
              : `Editar ${quantidade.toLocaleString("pt-BR")} em massa`}
          </AgnesButton>

          {podeAprovar && (
            <AgnesButton
              dataQa={`${dataQa}-aprovar`}
              variant="primary"
              className="bg-agnes-success hover:bg-[#006144]"
              startIcon={<Check className="h-4 w-4" />}
              loading={aprovando}
              onClick={onAprovar}
            >
              Aprovar {quantidade.toLocaleString("pt-BR")} em lote
            </AgnesButton>
          )}
        </div>
      </div>
    </div>
  );
}
